import { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, ListChecks, Plus, X, Pencil } from 'lucide-react';
import TaskFunctionLogic, { summarizeFunctionConfig } from './TaskFunctionLogic';
import { emptyTaskForm, taskToForm, formToPayload, validateTaskForm } from './crmTaskFormUtils';

/**
 * Mismas tareas del menú CRM → Tareas, en modal sobre la ficha del lead.
 * Crear + editar + completar + reglas Si/No/Entonces de funciones.
 */
export default function LeadTasksModal({ lead, open, onClose, onChanged }) {
  const [tasks, setTasks] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = lista; 'new' | uuid
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => emptyTaskForm());

  const load = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const [tRes, cRes, fRes, wRes] = await Promise.all([
        api.get(`/tenant/crm/tasks?status=all&lead_id=${lead.id}`),
        api.get('/tenant/catalog/items?page_size=100').catch(() => ({ data: [] })),
        api.get('/tenant/crm/task-functions').catch(() => ({ data: { items: [], module_enabled: false } })),
        api.get('/tenant/marketing/templates').catch(() => ({ data: [] })),
      ]);
      const tData = tRes.data;
      setTasks(Array.isArray(tData) ? tData : (tData?.items || []));
      const items = cRes.data?.items || cRes.data || [];
      setCatalog(Array.isArray(items) ? items : []);
      setFunctions(fRes.data?.items || fRes.data?.functions || []);
      setModuleEnabled(fRes.data?.module_enabled !== false && fRes.data?.enabled !== false);
      const wa = Array.isArray(wRes.data) ? wRes.data : (wRes.data?.items || []);
      setWaTemplates(wa.filter((t) => !t.external_status || t.external_status === 'APPROVED'));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar las tareas');
    }
    setLoading(false);
  }, [lead?.id]);

  useEffect(() => {
    if (open) {
      load();
      setEditingId(null);
      setForm(emptyTaskForm());
    }
  }, [open, load]);

  if (!open) return null;

  const assigneeHint = lead.assigned_to_name || lead.assigned_to_user_name || null;
  const catalogMap = Object.fromEntries(catalog.map((c) => [c.id, c]));
  const fnMap = Object.fromEntries(functions.map((f) => [f.key, f]));
  const selectedFn = functions.find((f) => f.key === form.function_key);

  const startNew = () => {
    setEditingId('new');
    setForm(emptyTaskForm({
      activity_label: '',
      title: '',
    }));
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setForm(taskToForm(task));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyTaskForm());
  };

  const save = async () => {
    const err = validateTaskForm(form);
    if (err) { toast.error(err); return; }
    if (form.function_key && selectedFn?.locked) {
      toast.error(selectedFn.lock_reason || 'Función no disponible en tu plan');
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form, { leadId: lead.id });
      if (editingId && editingId !== 'new') {
        await api.put(`/tenant/crm/tasks/${editingId}`, payload);
        toast.success('Tarea actualizada');
      } else {
        await api.post('/tenant/crm/tasks', payload);
        toast.success('Tarea creada (misma que en CRM → Tareas)');
      }
      cancelEdit();
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Error al guardar');
    }
    setSaving(false);
  };

  const complete = async (id) => {
    try {
      await api.patch(`/tenant/crm/tasks/${id}/complete`);
      toast.success('Tarea completada');
      if (editingId === id) cancelEdit();
      await load();
      onChanged?.();
    } catch {
      toast.error('No se pudo completar');
    }
  };

  const priceLabel = (c) => {
    if (!c) return '';
    if (c.price_min != null) return ` · desde $${c.price_min}`;
    if (c.price != null) return ` · $${c.price}`;
    return '';
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="lead-tasks-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col border border-[#E2E8F0]">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div>
            <h2 className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5">
              <ListChecks className="w-4 h-4 text-[#F72585]" /> Tareas de {lead.display_name || 'lead'}
            </h2>
            <p className="text-[11px] text-[#64748B] mt-0.5">
              Son las mismas del menú CRM → Tareas. Aquí las creas y editas sin salir del lead.
              {assigneeHint && (
                <> Responsable: <strong>{assigneeHint}</strong>.</>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[#F1F5F9]" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {!editingId && (
            <div className="flex justify-between items-center">
              <p className="text-xs text-[#64748B]">{tasks.length} tarea(s)</p>
              <Button size="sm" className="h-7 text-xs" onClick={startNew} data-testid="lead-tasks-modal-new">
                <Plus className="w-3 h-3 mr-1" /> Nueva
              </Button>
            </div>
          )}

          {editingId && (
            <div className="border rounded-lg p-3 space-y-2 bg-[#F8FAFC]" data-testid="lead-tasks-modal-form">
              <h3 className="text-xs font-bold text-[#0F172A]">
                {editingId === 'new' ? 'Nueva tarea' : 'Editar tarea'}
              </h3>
              <div>
                <label className="text-[11px] font-medium text-[#334155]">Título</label>
                <Input className="h-8 text-xs" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Llamar para cotizar" data-testid="lead-task-title" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#334155]">Fecha y hora</label>
                <Input className="h-8 text-xs" type="datetime-local" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="lead-task-due-at" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#334155]">
                  Oportunidad (lo que le quieres vender del catálogo)
                </label>
                <Select value={form.catalog_item_id || 'none'} onValueChange={(v) => setForm({ ...form, catalog_item_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Producto o servicio…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin ítem de catálogo</SelectItem>
                    {catalog.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || c.title}{priceLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">
                  No es otro lead: es el producto o servicio de tu catálogo (el valor de la venta).
                </p>
              </div>
              <div>
                <label className="text-[11px] font-medium text-[#334155]">Actividad</label>
                <Input className="h-8 text-xs" value={form.activity_label} onChange={(e) => setForm({ ...form, activity_label: e.target.value })} placeholder="Ej. Seguimiento comercial" />
              </div>
              {moduleEnabled && functions.length > 0 && (
                <div>
                  <label className="text-[11px] font-medium text-[#334155]">Función (automatización / IA)</label>
                  <Select
                    value={form.function_key || 'none'}
                    onValueChange={(v) => setForm({
                      ...form,
                      function_key: v === 'none' ? '' : v,
                      function_config: v === 'none' || v !== form.function_key ? {} : form.function_config,
                    })}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="lead-task-function"><SelectValue placeholder="Opcional…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin función</SelectItem>
                      {functions.map((f) => (
                        <SelectItem key={f.key} value={f.key} disabled={f.locked}>
                          {f.locked ? '🔒 ' : ''}{f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedFn && (
                    <TaskFunctionLogic
                      selectedFn={selectedFn}
                      functionConfig={form.function_config}
                      onConfigChange={(cfg) => setForm({ ...form, function_config: cfg })}
                      waTemplates={waTemplates}
                    />
                  )}
                </div>
              )}
              <div>
                <label className="text-[11px] font-medium text-[#334155]">Notas</label>
                <Input className="h-8 text-xs" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={cancelEdit}>Cancelar</Button>
                <Button size="sm" className="h-7 text-xs" onClick={save} disabled={saving} data-testid="lead-task-save">
                  {saving ? 'Guardando…' : editingId === 'new' ? 'Crear' : 'Guardar cambios'}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <p className="text-xs text-[#94A3B8]">Cargando…</p>
          ) : tasks.length === 0 && !editingId ? (
            <p className="text-xs text-[#94A3B8]">Sin tareas. Crea la primera sin salir del CRM.</p>
          ) : (
            <ul className="space-y-2">
              {tasks.map((t) => {
                const cat = t.catalog_item_id ? catalogMap[t.catalog_item_id] : null;
                const fn = t.function_key ? fnMap[t.function_key] : null;
                const ruleSum = summarizeFunctionConfig(fn, t.function_config);
                const isEditing = editingId === t.id;
                return (
                  <li
                    key={t.id}
                    className={`text-xs border rounded-lg px-3 py-2 ${isEditing ? 'ring-2 ring-[#F72585]/40 border-[#F72585]/40' : ''}`}
                    data-testid={`lead-task-row-${t.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        disabled={t.status === 'done'}
                        onClick={() => complete(t.id)}
                        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${t.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-[#CBD5E1]'}`}
                        title="Marcar completada"
                      >
                        {t.status === 'done' && <Check className="w-2.5 h-2.5 text-white" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={t.status === 'done' ? 'line-through text-[#94A3B8] font-medium' : 'text-[#0F172A] font-medium'}>
                          {t.title}
                        </p>
                        <div className="text-[10px] text-[#64748B] space-y-0.5 mt-0.5">
                          <p>
                            {t.due_at
                              ? new Date(t.due_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
                              : t.due_date
                                ? new Date(t.due_date).toLocaleDateString('es-MX')
                                : 'Sin fecha'}
                            {t.activity_label ? ` · ${t.activity_label}` : ''}
                            {t.status === 'done' ? ' · Completada' : ' · Pendiente'}
                          </p>
                          {cat && (
                            <p className="text-[#0F172A]">
                              Oportunidad: <strong>{cat.name}</strong>{priceLabel(cat)}
                            </p>
                          )}
                          {fn && (
                            <p>
                              Función: <strong>{fn.label || t.function_key}</strong>
                              {fn.medium === 'call' ? ' (llamada)' : fn.medium === 'whatsapp' ? ' (WhatsApp)' : ''}
                            </p>
                          )}
                          {ruleSum && <p className="text-[#94A3B8]">{ruleSum}</p>}
                          {t.description && <p className="text-[#94A3B8]">Notas: {t.description}</p>}
                        </div>
                      </div>
                      {t.status !== 'done' && (
                        <button
                          type="button"
                          onClick={() => startEdit(t)}
                          className="p-1 rounded hover:bg-[#F1F5F9] text-[#64748B]"
                          title="Editar"
                          data-testid={`lead-task-edit-${t.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
